// @ts-nocheck
// frontend/modules/drafts/hooks/useDrafts.ts
import { useCallback, useEffect } from 'react';
import { useDraftsStore } from '../stores/drafts-store';
import { DraftProcess, DraftProcessCreateRequest, DraftProcessUpdateRequest } from '../types';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import axios from 'axios';

// Import API client hooks from Orval-generated API clients
import {
  useGetAllDraftProcesses,
  useGetDraftProcessesByUserId,
  useGetPendingDraftProcessesByUserId,
  useCreateDraftProcess,
  useUpdateDraftProcess,
  useCompleteDraftProcess,
  useCancelDraftProcess,
  useDeleteDraftProcess
} from '@/shared/api/client/draft-process-controller';

export const useDrafts = (userId?: string) => {
  const { dark } = useTheme();
  const {
    draftProcesses,
    filteredDraftProcesses,
    isLoading,
    error,
    searchTerm,
    selectedStatus,
    activeDraftProcess,

    setDraftProcesses,
    setIsLoading,
    setError,
    setSearchTerm,
    setSelectedStatus,
    setActiveDraftProcess,
    filterDraftProcesses
  } = useDraftsStore();

  // Fetch all draft processes
  const {
    data: allDraftsData,
    isLoading: isAllDraftsLoading,
    refetch: refetchAllDrafts,
    error: allDraftsError
  } = useGetAllDraftProcesses();

  // Fetch user-specific draft processes if userId is provided
  const {
    data: userDraftsData,
    isLoading: isUserDraftsLoading,
    refetch: refetchUserDrafts,
    error: userDraftsError
  } = useGetDraftProcessesByUserId(
    userId || '',
    { query: { enabled: !!userId } }
  );

  // Fetch pending drafts for user if userId is provided
  const {
    data: pendingDraftsData,
    isLoading: isPendingDraftsLoading,
    refetch: refetchPendingDrafts,
    error: pendingDraftsError
  } = useGetPendingDraftProcessesByUserId(
    userId || '',
    { query: { enabled: !!userId } }
  );

  // Create draft process mutation
  const { mutateAsync: createDraft, isPending: isCreating } = useCreateDraftProcess();

  // Update draft process mutation
  const { mutateAsync: updateDraft, isPending: isUpdating } = useUpdateDraftProcess();

  // Complete draft process mutation
  const { mutateAsync: completeDraft, isPending: isCompleting } = useCompleteDraftProcess();

  // Cancel draft process mutation
  const { mutateAsync: cancelDraft, isPending: isCanceling } = useCancelDraftProcess();

  // Delete draft process mutation
  const { mutateAsync: deleteDraft, isPending: isDeleting } = useDeleteDraftProcess();

  // Function to safely extract data from API response
  const extractDraftData = useCallback((responseData: any): DraftProcess[] => {
    if (!responseData) return [];
    
    // If it's already an array, use it
    if (Array.isArray(responseData)) return responseData;
    
    // If it has a data property that's an array, use that
    if (responseData.data && Array.isArray(responseData.data)) return responseData.data;
    
    // If it has a content property that's an array, use that (common Spring Data pattern)
    if (responseData.content && Array.isArray(responseData.content)) return responseData.content;
    
    console.warn('Could not extract draft data from response:', responseData);
    return [];
  }, []);

  // Add useEffect hooks to update the store when data changes
  useEffect(() => {
    if (allDraftsData) {
      const drafts = extractDraftData(allDraftsData);
      setDraftProcesses(drafts);
    }
  }, [allDraftsData, setDraftProcesses, extractDraftData]);

  useEffect(() => {
    if (userDraftsData && userId) {
      const drafts = extractDraftData(userDraftsData);
      setDraftProcesses(drafts);
    }
  }, [userDraftsData, userId, setDraftProcesses, extractDraftData]);

  useEffect(() => {
    if (allDraftsError || userDraftsError) {
      const errorMessage = allDraftsError?.message || userDraftsError?.message || 'Error loading drafts';
      setError(errorMessage);
    }
  }, [allDraftsError, userDraftsError, setError]);

  // Function to load draft processes based on context
  const loadDraftProcesses = useCallback(async () => {
    try {
      if (userId) {
        await refetchUserDrafts();
      } else {
        await refetchAllDrafts();
      }
    } catch (error) {
      console.error('Error loading draft processes:', error);
      showErrorNotification('Error', 'Failed to load draft processes', dark);
    }
  }, [userId, refetchUserDrafts, refetchAllDrafts, dark]);

  // Function to load pending draft processes for a user
  const loadPendingDraftProcesses = useCallback(async () => {
    if (!userId) return;
    try {
      await refetchPendingDrafts();
    } catch (error) {
      console.error('Error loading pending draft processes:', error);
      showErrorNotification('Error', 'Failed to load pending draft processes', dark);
    }
  }, [userId, refetchPendingDrafts, dark]);

  // Create a new draft process
  const createDraftProcess = useCallback(async (draftData: DraftProcessCreateRequest) => {
    try {
      const response = await createDraft({ data: draftData });
      showSuccessNotification('Success', 'Draft process created successfully', dark);
      await loadDraftProcesses();
      return response.data;
    } catch (error) {
      console.error('Error creating draft process:', error);
      showErrorNotification('Error', 'Failed to create draft process', dark);
      throw error;
    }
  }, [createDraft, loadDraftProcesses, dark]);

  // Update an existing draft process
  const updateDraftProcess = useCallback(async (id: string, updateData: DraftProcessUpdateRequest) => {
    try {
      const response = await updateDraft({
        id,
        data: updateData
      });
      showSuccessNotification('Success', 'Draft process updated successfully', dark);
      await loadDraftProcesses();
      return response.data;
    } catch (error) {
      console.error('Error updating draft process:', error);
      showErrorNotification('Error', 'Failed to update draft process', dark);
      throw error;
    }
  }, [updateDraft, loadDraftProcesses, dark]);

  // Complete a draft process
  const completeDraftProcess = useCallback(async (id: string, documentIds?: string) => {
    try {
      const response = await completeDraft({
        id,
        params: { documentIds }
      });
      showSuccessNotification('Success', 'Draft process completed successfully', dark);
      await loadDraftProcesses();
      return response.data;
    } catch (error) {
      console.error('Error completing draft process:', error);
      showErrorNotification('Error', 'Failed to complete draft process', dark);
      throw error;
    }
  }, [completeDraft, loadDraftProcesses, dark]);

  // Cancel a draft process
  const cancelDraftProcess = useCallback(async (id: string) => {
    try {
      const response = await cancelDraft({ id });
      showSuccessNotification('Success', 'Draft process canceled successfully', dark);
      await loadDraftProcesses();
      return response.data;
    } catch (error) {
      console.error('Error canceling draft process:', error);
      showErrorNotification('Error', 'Failed to cancel draft process', dark);
      throw error;
    }
  }, [cancelDraft, loadDraftProcesses, dark]);

  // Delete a draft process
  const deleteDraftProcess = useCallback(async (id: string) => {
    try {
      await deleteDraft({ id });
      showSuccessNotification('Success', 'Draft process deleted successfully', dark);
      await loadDraftProcesses();
    } catch (error) {
      console.error('Error deleting draft process:', error);
      showErrorNotification('Error', 'Failed to delete draft process', dark);
      throw error;
    }
  }, [deleteDraft, loadDraftProcesses, dark]);

  // Function to check if a user has a draft for a specific reference code
  const checkDraftsByReferenceCode = useCallback(async (referenceCode: string): Promise<DraftProcess[]> => {
    if (!userId) return [];
    
    try {
      setIsLoading(true);
      // Since we don't have the Orval-generated hook for this endpoint yet, 
      // use axios directly for now
      const response = await axios.get(`/api/draftprocess/user/${userId}/reference/${referenceCode}`);
      return response.data;
    } catch (error) {
      console.error('Error checking drafts by reference code:', error);
      setError('Failed to check drafts by reference code');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [userId, setIsLoading, setError]);

  // Function to check if a user has pending drafts for a specific reference code
  const checkPendingDraftsByReferenceCode = useCallback(async (referenceCode: string): Promise<DraftProcess[]> => {
    if (!userId) return [];
    
    try {
      setIsLoading(true);
      // Since we don't have the Orval-generated hook for this endpoint yet, 
      // use axios directly for now
      const response = await axios.get(`/api/draftprocess/user/${userId}/reference/${referenceCode}/pending`);
      return response.data;
    } catch (error) {
      console.error('Error checking pending drafts by reference code:', error);
      setError('Failed to check pending drafts by reference code');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [userId, setIsLoading, setError]);

  return {
    // State
    draftProcesses,
    filteredDraftProcesses,
    isLoading: isLoading || isAllDraftsLoading || isUserDraftsLoading || isPendingDraftsLoading ||
               isCreating || isUpdating || isCompleting || isCanceling || isDeleting,
    error,
    searchTerm,
    selectedStatus,
    activeDraftProcess,

    // Actions
    setSearchTerm,
    setSelectedStatus,
    setActiveDraftProcess,
    loadDraftProcesses,
    loadPendingDraftProcesses,
    createDraftProcess,
    updateDraftProcess,
    completeDraftProcess,
    cancelDraftProcess,
    deleteDraftProcess,
    checkDraftsByReferenceCode,
    checkPendingDraftsByReferenceCode
  };
};
